import React, { FunctionComponent, useMemo } from 'react';
import TrainingService from 'services/training.service';
import { TrainingSettingsTypeEnum } from '@halter-corp/training-service-client';
import DataTable from 'application/components/data-table';
import { useSelector } from 'react-redux';
import { getCurrentFarm } from 'store/selectors/farm-context.selectors';
import BusinessService from 'services/business.service';
import { keyBy } from 'lodash';
import styled from 'styled-components';
import { prettyPrintProfileType } from '../utils';

const FarmName = styled.div`
  font-size: 16px;
  font-weight: 500;
  opacity: 0.75;
  padding: 8px 0 4px;
`;

const TablesWrapper = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  & > * {
    flex: 1;
  }
`;

const ProfileLink = styled.a`
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

type BaseRow = { type: TrainingSettingsTypeEnum; profileName: string; profileId?: string };

const ALL_TYPES = new Set(Object.values(TrainingSettingsTypeEnum));

const NON_CROP_TYPES = Object.values(TrainingSettingsTypeEnum)
  .filter((type) => !type.endsWith('_CROP'))
  .sort((a, b) => {
    const aHasCrop = ALL_TYPES.has(`${a}_CROP` as TrainingSettingsTypeEnum);
    const bHasCrop = ALL_TYPES.has(`${b}_CROP` as TrainingSettingsTypeEnum);
    if (aHasCrop !== bHasCrop) return aHasCrop ? -1 : 1;
    // EXIT_PADDOCK_DRAFTING sorts last among types without a crop version
    const aDrafting = a === TrainingSettingsTypeEnum.EXIT_PADDOCK_DRAFTING;
    const bDrafting = b === TrainingSettingsTypeEnum.EXIT_PADDOCK_DRAFTING;
    if (aDrafting === bDrafting) return 0;
    return aDrafting ? 1 : -1;
  });

const WITH_CROP_TYPES = NON_CROP_TYPES.filter((type) =>
  ALL_TYPES.has(`${type}_CROP` as TrainingSettingsTypeEnum)
);

const renderProfile = (row: BaseRow) =>
  row.profileId ? (
    <ProfileLink href={`/training/profiles/${row.profileId}`} target="_blank" rel="noreferrer">
      {row.profileName}
    </ProfileLink>
  ) : (
    row.profileName
  );

const columns = [
  { name: 'Type', render: (row: BaseRow) => prettyPrintProfileType(row.type) },
  { name: 'Profile', render: renderProfile },
];

const AppliedProfilesTable: FunctionComponent = () => {
  const appliedProfiles = TrainingService.useSelectAppliedProfiles();
  const currentFarm = useSelector(getCurrentFarm);
  const farms = BusinessService.useFetchFarmList();

  const keyedFarms = useMemo(() => keyBy(farms, ({ id }) => id), [farms]);

  const { baseRows, cropRows } = useMemo(() => {
    const getProfileRow = (type: TrainingSettingsTypeEnum): Pick<BaseRow, 'profileName' | 'profileId'> => {
      const match = appliedProfiles.find((ap) => ap.profile?.type === type)?.profile;
      return { profileName: match?.name ?? '—', profileId: match?.id };
    };

    return {
      baseRows: NON_CROP_TYPES.map((type) => ({ type, ...getProfileRow(type) })),
      cropRows: WITH_CROP_TYPES.map((type) => {
        const cropType = `${type}_CROP` as TrainingSettingsTypeEnum;
        return { type: cropType, ...getProfileRow(cropType) };
      }),
    };
  }, [appliedProfiles]);

  return (
    <>
      <FarmName>{keyedFarms[currentFarm]?.name ?? currentFarm}</FarmName>
      <TablesWrapper>
        <DataTable<BaseRow> data={baseRows} size="small" tableSortDisabled columns={columns} />
        <DataTable<BaseRow> data={cropRows} size="small" tableSortDisabled columns={columns} />
      </TablesWrapper>
    </>
  );
};

export default AppliedProfilesTable;
